import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Check, Plus, Trash2, Wallet, Plug } from 'lucide-react';
import { formatarPct, reputacaoDoNivelML, ROTULO_REPUTACAO } from '@profitlens/calculo';
import { Cabecalho } from '../componentes/Cabecalho';
import { Carregando, Erro, Vazio } from '../componentes/Estados';
import {
  Campo,
  CampoInteiro,
  CampoPercentual,
  CampoQuantidade,
  CampoReais,
  Texto as CampoTexto,
} from '../componentes/Campos';
import { useAuth } from '../contextos/AuthContext';
import { useCategorias } from '../servicos/categorias';
import { AbaCategorias } from './ConfigCategorias';
import { AbaCustosFixos } from './ConfigCustosFixos';
import { AbaFrete } from './ConfigFrete';
import { AbaAgenda } from './ConfigAgenda';
import {
  conectarMercadoLivre,
  desconectarML,
  sondarBusca,
  sondarConta,
  statusConexaoML,
  type StatusConexao,
} from '../servicos/mercadolivre';
import {
  criteriosPadrao,
  faltandoNoPerfil,
  perfilVazio,
  removerPerfilCusto,
  salvarCriterios,
  salvarPerfilCusto,
  useCriterios,
  usePerfisCusto,
  type RascunhoCriterios,
  type RascunhoPerfil,
} from '../servicos/configuracoes';

const ABAS = [
  'Categorias',
  'Perfis de custo',
  'Custos fixos',
  'Frete',
  'Agenda',
  'Critérios',
  'Integrações',
] as const;
type Aba = (typeof ABAS)[number];

export function Configuracoes() {
  const [aba, setAba] = useState<Aba>('Categorias');
  // A conexão é lida uma vez aqui e emprestada às abas que precisam: a de frete
  // usa a reputação da conta para mostrar os valores que VOCÊ paga.
  const [conexao, setConexao] = useState<StatusConexao | null>(null);
  useEffect(() => {
    statusConexaoML()
      .then(setConexao)
      .catch(() => setConexao(null));
  }, []);

  return (
    <div className="flex flex-col gap-5">
      <Cabecalho
        titulo="Configurações"
        descricao="O que a análise usa para decidir. Só o administrador mexe aqui."
      />

      <div className="flex flex-wrap gap-1 border-b border-regua-2">
        {ABAS.map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => setAba(a)}
            className={`-mb-px border-b-2 px-3 py-1.5 text-sm font-medium ${
              aba === a
                ? 'border-marca text-marca'
                : 'border-transparent text-tinta-2 hover:text-tinta'
            }`}
          >
            {a}
          </button>
        ))}
      </div>

      {aba === 'Categorias' && <AbaCategorias />}
      {aba === 'Perfis de custo' && <AbaPerfis />}
      {aba === 'Custos fixos' && <AbaCustosFixos />}
      {aba === 'Frete' && <AbaFrete conexao={conexao} />}
      {aba === 'Agenda' && <AbaAgenda />}
      {aba === 'Critérios' && <AbaCriterios />}
      {aba === 'Integrações' && <AbaIntegracoes />}
    </div>
  );
}

// ---------- perfis de custo ----------

function AbaPerfis() {
  const { perfis, carregando, erro } = usePerfisCusto();
  const { perfil: usuario } = useAuth();
  const [editando, setEditando] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);

  if (carregando) return <Carregando />;
  if (erro) return <Erro titulo="Não foi possível carregar os perfis" detalhe={erro} />;

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-md border-l-3 border-est bg-est/10 px-4 py-3 text-sm text-tinta-2">
        <p>
          <strong className="text-tinta">Um campo em branco não é zero.</strong> Comissão, tarifa e
          alíquotas dependem do seu regime tributário e da categoria do anúncio — o sistema não tem
          como adivinhar nenhum dos dois, e inventar um valor produziria um lucro que parece
          conferido e está inflado.
        </p>
        <p className="mt-1.5">
          Enquanto faltar algum, a análise sai como{' '}
          <strong className="text-tinta">Dados insuficientes</strong>. Isso é o resultado correto,
          não uma falha do sistema.
        </p>
      </div>

      {perfis.length === 0 && !novo ? (
        <Vazio
          icone={<Wallet className="size-8" />}
          titulo="Nenhum perfil de custo"
          descricao="Um perfil reúne o que você paga para vender: comissão do marketplace, tarifa, impostos e devoluções. Sem pelo menos um, nenhuma análise fecha a conta."
          acao={
            <button
              type="button"
              onClick={() => setNovo(true)}
              className="rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90"
            >
              Criar o primeiro
            </button>
          }
        />
      ) : (
        <>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                setNovo(true);
                setEditando(null);
              }}
              className="inline-flex items-center gap-1.5 rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90"
            >
              <Plus className="size-4" aria-hidden />
              Novo perfil
            </button>
          </div>

          {novo && (
            <FormularioPerfil
              inicial={perfilVazio()}
              usuarioId={usuario?.id ?? ''}
              aoConcluir={() => setNovo(false)}
            />
          )}

          <ul className="flex flex-col gap-2">
            {perfis.map((p) => {
              const faltando = faltandoNoPerfil(p);
              const aberto = editando === p.id;
              return (
                <li key={p.id} className="rounded-md border border-regua-2 bg-superficie">
                  <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <span className="font-medium">{p.nome}</span>
                    {p.padrao && (
                      <span className="rounded bg-marca/12 px-1.5 py-0.5 text-[0.65rem] font-semibold text-marca">
                        PADRÃO
                      </span>
                    )}
                    {faltando.length === 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs text-c-via">
                        <Check className="size-3.5" aria-hidden />
                        completo
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-xs text-c-res"
                        title={`Sem isto a análise não fecha a conta: ${faltando.join(', ')}.`}
                      >
                        <AlertTriangle className="size-3.5" aria-hidden />
                        falta {faltando.join(', ')}
                      </span>
                    )}

                    <div className="ml-auto flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => setEditando(aberto ? null : p.id)}
                        className="rounded border border-regua-2 px-2.5 py-1 text-xs hover:border-marca hover:text-marca"
                      >
                        {aberto ? 'Fechar' : 'Editar'}
                      </button>
                      <button
                        type="button"
                        onClick={() => removerPerfilCusto(p.id)}
                        aria-label={`Remover ${p.nome}`}
                        className="rounded border border-regua-2 px-2 py-1 text-tinta-3 hover:border-pend hover:text-pend"
                      >
                        <Trash2 className="size-3.5" aria-hidden />
                      </button>
                    </div>
                  </div>

                  {aberto && (
                    <FormularioPerfil
                      id={p.id}
                      inicial={p}
                      usuarioId={usuario?.id ?? ''}
                      aoConcluir={() => setEditando(null)}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

function FormularioPerfil({
  id,
  inicial,
  usuarioId,
  aoConcluir,
}: {
  id?: string;
  inicial: RascunhoPerfil;
  usuarioId: string;
  aoConcluir: () => void;
}) {
  const [d, setD] = useState<RascunhoPerfil>(() => ({ ...inicial }));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const editar = <K extends keyof RascunhoPerfil>(campo: K, valor: RascunhoPerfil[K]) =>
    setD((x) => ({ ...x, [campo]: valor }));

  const faltando = faltandoNoPerfil(d);

  async function salvar(e: FormEvent) {
    e.preventDefault();
    if (!d.nome.trim()) {
      setErro('Dê um nome ao perfil — é por ele que você vai escolhê-lo na análise.');
      return;
    }
    setErro(null);
    setSalvando(true);
    try {
      await salvarPerfilCusto(id ?? null, d, usuarioId);
      aoConcluir();
    } catch (ex) {
      setErro(ex instanceof Error ? ex.message : 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form
      onSubmit={salvar}
      className="flex flex-col gap-4 border-t border-regua bg-superficie px-4 py-4"
    >
      {erro && <Erro titulo={erro} />}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <CampoTexto
          rotulo="Nome do perfil"
          valor={d.nome}
          aoMudar={(v) => editar('nome', v ?? '')}
          dica="Ex.: Mercado Livre Clássico"
        />
        <Campo rotulo="Usar por padrão">
          <label className="flex items-center gap-2 py-1.5 text-sm">
            <input
              type="checkbox"
              checked={d.padrao}
              onChange={(e) => editar('padrao', e.target.checked)}
            />
            <span className="text-tinta-2">A análise começa com este perfil</span>
          </label>
        </Campo>
      </div>

      <div className="rounded border-l-3 border-est bg-est/10 px-3 py-2 text-xs text-tinta-2">
        <strong className="text-tinta">Comissão e tarifa fixa não ficam aqui.</strong> Elas dependem
        da categoria <em>e do preço</em>, então a análise consulta cada uma no Mercado Livre, para o
        preço exato. O que este perfil guarda é o que é fato sobre a <em>sua</em> operação.
      </div>

      <fieldset className="flex flex-col gap-3">
        <legend className="font-mono text-[0.62rem] tracking-wider text-marca uppercase">
          O que se paga para vender
        </legend>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Campo
            rotulo="Reputação no Mercado Livre"
            dica="Multiplica o Custo dos Envios. Se a conta do Mercado Livre estiver conectada, a reputação dela manda e este campo vira reserva."
          >
            <select
              value={d.reputacaoML}
              onChange={(e) => editar('reputacaoML', e.target.value as RascunhoPerfil['reputacaoML'])}
              className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca"
            >
              {(['VERDE', 'AMARELA', 'LARANJA_VERMELHA'] as const).map((r) => (
                <option key={r} value={r}>
                  {ROTULO_REPUTACAO[r]}
                </option>
              ))}
            </select>
          </Campo>
          <CampoPercentual
            rotulo="Imposto sobre a venda"
            bps={d.impostoVendaBps}
            aoMudar={(v) => editar('impostoVendaBps', v)}
            dica="Depende do seu regime tributário."
          />
          <CampoPercentual
            rotulo="Devoluções esperadas"
            bps={d.devolucaoBps}
            aoMudar={(v) => editar('devolucaoBps', v)}
          />
          <CampoPercentual
            rotulo="Custo financeiro"
            bps={d.custoFinanceiroBps}
            aoMudar={(v) => editar('custoFinanceiroBps', v)}
            dica="Antecipação de recebíveis, taxa de parcelamento."
          />
          <CampoReais
            rotulo="Frete subsidiado por unidade"
            cents={d.freteSubsidiadoCents}
            aoMudar={(v) => editar('freteSubsidiadoCents', v ?? 0)}
          />
          <CampoReais
            rotulo="Custos fixos de venda por unidade"
            cents={d.custosVendaFixosCents}
            aoMudar={(v) => editar('custosVendaFixosCents', v ?? 0)}
            dica="Embalagem, expedição, marketing, armazenagem de saída."
          />
          <CampoReais
            rotulo="Desses, quanto sai antes de vender"
            cents={d.custosVendaAntecipadosCents}
            aoMudar={(v) => editar('custosVendaAntecipadosCents', v ?? 0)}
            dica="Entra no capital necessário."
          />
          <CampoPercentual
            rotulo="Perda e avaria"
            bps={d.perdaBps}
            aoMudar={(v) => editar('perdaBps', v ?? 0)}
            dica="Reduz a quantidade vendável; não é linha de custo."
          />
        </div>
      </fieldset>

      <CampoTexto
        rotulo="Observações"
        valor={d.observacoes}
        aoMudar={(v) => editar('observacoes', v)}
      />

      {faltando.length > 0 && (
        <p className="rounded border-l-3 border-c-res bg-c-res/10 px-3 py-2 text-xs text-tinta-2">
          Ainda falta: <strong>{faltando.join(', ')}</strong>. Dá para salvar assim — a análise vai
          apontar exatamente o que está faltando em vez de entregar um número errado.
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={salvando}
          className="rounded bg-marca px-4 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
        >
          {salvando ? 'Salvando…' : 'Salvar perfil'}
        </button>
        <button
          type="button"
          onClick={aoConcluir}
          className="rounded border border-regua-2 px-4 py-1.5 text-sm hover:border-marca"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

// ---------- critérios ----------

function AbaCriterios() {
  const { criterios, carregando } = useCriterios();
  const { perfil } = useAuth();
  const [d, setD] = useState<RascunhoCriterios>(criteriosPadrao());
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    if (criterios) {
      const { id: _i, criadoEm, criadoPor, atualizadoEm, atualizadoPor, ...resto } = criterios;
      setD(resto);
    }
  }, [criterios?.id, criterios?.atualizadoEm]);

  if (carregando) return <Carregando />;

  const editar = <K extends keyof RascunhoCriterios>(campo: K, valor: RascunhoCriterios[K]) => {
    setSalvo(false);
    setD((x) => ({ ...x, [campo]: valor }));
  };

  async function salvar(e: FormEvent) {
    e.preventDefault();
    if (!perfil) return;
    setErro(null);
    setSalvando(true);
    try {
      await salvarCriterios(d, perfil.id, criterios !== null);
      setSalvo(true);
    } catch (ex) {
      setErro(ex instanceof Error ? ex.message : 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={salvar} className="flex flex-col gap-4">
      <div className="rounded-md border-l-3 border-est bg-est/10 px-4 py-3 text-sm text-tinta-2">
        <strong className="text-tinta">Estes números vêm preenchidos, e os custos não.</strong> A
        diferença é proposital: "margem mínima de 15%" é uma política sua, um ponto de partida
        razoável que você ajusta. Já a comissão do marketplace é um fato sobre a sua operação —
        inventar um valor lá seria inventar dinheiro.
      </div>

      {criterios === null && (
        <p className="text-xs text-tinta-3">
          Ainda não salvos: a análise está usando estes valores de partida. Salve para deixá-los
          registrados.
        </p>
      )}

      <fieldset className="flex flex-col gap-3">
        <legend className="font-mono text-[0.62rem] tracking-wider text-marca uppercase">
          Mínimos para ser viável
        </legend>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <CampoPercentual
            rotulo="Margem mínima"
            bps={d.margemMinimaBps}
            aoMudar={(v) => editar('margemMinimaBps', v ?? 0)}
          />
          <CampoPercentual
            rotulo="ROI mínimo"
            bps={d.roiMinimoBps}
            aoMudar={(v) => editar('roiMinimoBps', v ?? 0)}
          />
          <CampoReais
            rotulo="Capital máximo por operação"
            cents={d.capitalMaximoCents}
            aoMudar={(v) => editar('capitalMaximoCents', v ?? 0)}
          />
          <CampoInteiro
            rotulo="Prazo máximo de retorno"
            valor={d.prazoMaxRetornoDias}
            aoMudar={(v) => editar('prazoMaxRetornoDias', v ?? 0)}
            sufixo="dias"
          />
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-3">
        <legend className="font-mono text-[0.62rem] tracking-wider text-marca uppercase">
          Qualidade do dado exigida
        </legend>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <CampoInteiro
            rotulo="Mínimo de anúncios comparáveis"
            valor={d.minAnunciosComp}
            aoMudar={(v) => editar('minAnunciosComp', v ?? 0)}
          />
          <CampoInteiro
            rotulo="Idade máxima da cotação"
            valor={d.idadeMaxCotacaoDias}
            aoMudar={(v) => editar('idadeMaxCotacaoDias', v ?? 0)}
            sufixo="dias"
          />
          <CampoQuantidade
            rotulo="Estoque mínimo do fornecedor"
            milli={d.estoqueMinimoMilli}
            aoMudar={(v) => editar('estoqueMinimoMilli', v ?? 0)}
          />
          <CampoPercentual
            rotulo="Dispersão máxima entre anúncios"
            bps={d.dispersaoMaximaBps}
            aoMudar={(v) => editar('dispersaoMaximaBps', v ?? 0)}
            dica="Acima disto, o preço de mercado não é confiável e a análise pede revisão."
          />
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-3">
        <legend className="font-mono text-[0.62rem] tracking-wider text-marca uppercase">
          Limiares de “excelente”
        </legend>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <CampoPercentual
            rotulo="Margem excelente"
            bps={d.margemExcelenteBps}
            aoMudar={(v) => editar('margemExcelenteBps', v ?? 0)}
          />
          <CampoPercentual
            rotulo="ROI excelente"
            bps={d.roiExcelenteBps}
            aoMudar={(v) => editar('roiExcelenteBps', v ?? 0)}
          />
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-3">
        <legend className="font-mono text-[0.62rem] tracking-wider text-marca uppercase">
          Cenários
        </legend>
        <p className="text-xs text-tinta-3">
          A análise sempre calcula três: pessimista, provável e otimista. Estes números definem o
          quanto os extremos se afastam do provável.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <CampoPercentual
            rotulo="Queda de preço no pessimista"
            bps={d.deltaPessimistaBps}
            aoMudar={(v) => editar('deltaPessimistaBps', v ?? 0)}
          />
          <CampoPercentual
            rotulo="Alta de preço no otimista"
            bps={d.deltaOtimistaBps}
            aoMudar={(v) => editar('deltaOtimistaBps', v ?? 0)}
          />
          <CampoPercentual
            rotulo="Quanto se vende no pessimista"
            bps={d.pctVendidoPessimistaBps}
            aoMudar={(v) => editar('pctVendidoPessimistaBps', v ?? 0)}
            dica="Do que estava disponível para vender."
          />
        </div>
      </fieldset>

      {erro && <Erro titulo={erro} />}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={salvando}
          className="rounded bg-marca px-4 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
        >
          {salvando ? 'Salvando…' : 'Salvar critérios'}
        </button>
        {salvo && (
          <span className="inline-flex items-center gap-1 text-xs text-c-via">
            <Check className="size-3.5" aria-hidden />
            salvo
          </span>
        )}
        <button
          type="button"
          onClick={() => {
            setD(criteriosPadrao());
            setSalvo(false);
          }}
          className="ml-auto text-xs text-tinta-3 underline-offset-2 hover:text-marca hover:underline"
        >
          Voltar aos valores de partida
        </button>
      </div>
    </form>
  );
}

// ---------- integrações ----------

/**
 * O que está ligado e o que não está — dito com honestidade.
 *
 * Uma tela de integrações que só mostra o que funciona esconde o motivo de a
 * análise sair incompleta. Aqui cada pendência aparece com o número dela no
 * planejamento, para o assunto ser rastreável em vez de virar "tá lento".
 */
/**
 * A ligação com a conta do Mercado Livre.
 *
 * Catálogo e tarifa já funcionam só com a aplicação — são dados do site. O que
 * exige autorização do dono é o que é da conta: reputação, anúncios e a tabela
 * de frete negociada.
 */
function CartaoMercadoLivre() {
  const [status, setStatus] = useState<StatusConexao | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [diagnostico, setDiagnostico] = useState<string | null>(null);
  const [ean, setEan] = useState('');
  const [parametros, setParametros] = useSearchParams();

  const recarregar = () =>
    statusConexaoML()
      .then(setStatus)
      .catch((e: { message?: string }) => setErro(e.message ?? 'Não deu para ler o estado.'));

  useEffect(() => {
    void recarregar();
  }, []);

  // O callback do OAuth devolve o navegador para cá com o resultado na URL.
  const resultado = parametros.get('ml');
  useEffect(() => {
    if (!resultado) return;
    void recarregar();
    const p = new URLSearchParams(parametros);
    p.delete('ml');
    p.delete('motivo');
    setParametros(p, { replace: true });
  }, [resultado]);

  const reputacaoDaConta = reputacaoDoNivelML(status?.reputacaoNivel);

  const emDia =
    status?.expiraEm !== null && status?.expiraEm !== undefined
      ? new Date(status.expiraEm) > new Date()
      : null;

  return (
    <li className="flex flex-col gap-2 rounded-md border border-regua-2 bg-superficie px-4 py-3">
      <span className="flex flex-wrap items-center gap-2">
        <Plug className="size-4 text-tinta-3" aria-hidden />
        <span className="font-medium">Conta do Mercado Livre</span>
        <span
          className={`rounded px-1.5 py-0.5 font-mono text-[0.62rem] font-semibold ${
            status?.conectado ? 'text-c-via bg-c-via/12' : 'text-c-res bg-c-res/12'
          }`}
        >
          {status?.conectado ? 'CONECTADA' : 'NÃO CONECTADA'}
        </span>
      </span>

      {resultado === 'conectado' && (
        <p className="text-xs text-c-via">Conta conectada.</p>
      )}
      {(resultado === 'erro' || resultado === 'recusado') && (
        <p className="text-xs text-pend">
          {resultado === 'recusado' ? 'A autorização foi recusada.' : 'Não deu certo.'}{' '}
          {parametros.get('motivo')}
        </p>
      )}

      {status?.conectado ? (
        <p className="text-xs text-tinta-2">
          Ligada à conta <strong className="text-tinta">{status.apelido ?? status.usuarioML}</strong>.
          {reputacaoDaConta && (
            <>
              {' '}
              Reputação{' '}
              <strong className="text-tinta">{ROTULO_REPUTACAO[reputacaoDaConta]}</strong>, vinda do
              Mercado Livre — é ela que a análise usa no frete, no lugar do campo do perfil.
            </>
          )}
          {status.reputacaoNivel && !reputacaoDaConta && (
            <span className="text-c-res">
              {' '}
              O Mercado Livre devolveu o nível “{status.reputacaoNivel}”, que eu não sei traduzir.
              A análise vai usar o campo do perfil.
            </span>
          )}
          {emDia === false && (
            <span className="text-c-res"> O acesso venceu e será renovado no próximo uso.</span>
          )}
        </p>
      ) : (
        <p className="text-xs text-tinta-2">
          Categoria e tarifa já funcionam sem isto. Conectar a conta acrescenta o que é dela:
          reputação, anúncios e a tabela de frete negociada — hoje preenchida à mão, com data.
        </p>
      )}

      {erro && <p className="text-xs text-pend">{erro}</p>}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={ocupado}
          onClick={async () => {
            setErro(null);
            setOcupado(true);
            try {
              await conectarMercadoLivre();
            } catch (e) {
              setErro((e as { message?: string }).message ?? 'Não deu para iniciar a conexão.');
              setOcupado(false);
            }
          }}
          className="rounded bg-marca px-3 py-1.5 text-xs font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
        >
          {status?.conectado ? 'Reconectar' : 'Conectar conta'}
        </button>
        {status?.conectado && (
          <>
            <button
              type="button"
              disabled={ocupado}
              onClick={async () => {
                setErro(null);
                setDiagnostico(null);
                setOcupado(true);
                try {
                  setDiagnostico(JSON.stringify(await sondarConta(), null, 2));
                } catch (e) {
                  setErro((e as { message?: string }).message ?? 'A sonda falhou.');
                } finally {
                  setOcupado(false);
                }
              }}
              className="rounded border border-regua-2 px-3 py-1.5 text-xs hover:border-marca hover:text-marca disabled:opacity-60"
            >
              {ocupado ? 'Perguntando…' : 'Diagnóstico'}
            </button>
            <button
              type="button"
              disabled={ocupado}
              onClick={async () => {
                setOcupado(true);
                try {
                  await desconectarML();
                  await recarregar();
                } finally {
                  setOcupado(false);
                }
              }}
              className="rounded border border-regua-2 px-3 py-1.5 text-xs hover:border-pend hover:text-pend disabled:opacity-60"
            >
              Desconectar
            </button>
          </>
        )}
      </div>

      {/*
        A pergunta que decide a pesquisa de mercado: dá para ver anúncio de
        OUTRO vendedor pelo código de barras? Sem isso, comparar preço com a
        concorrência continua sendo digitação.
      */}
      <div className="mt-1 flex flex-wrap items-end gap-2 rounded border border-regua bg-superficie-2 px-3 py-2">
        <label className="flex flex-1 flex-col gap-1">
          <span className="text-[0.65rem] font-medium text-tinta-2">
            Testar busca de mercado por EAN
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={ean}
            onChange={(e) => setEan(e.target.value)}
            placeholder="código de barras de um produto que você vende"
            className="w-full rounded border border-regua-2 bg-papel px-2 py-1 font-mono text-xs outline-none focus:border-marca"
          />
        </label>
        <button
          type="button"
          disabled={ocupado || ean.replace(/\D/g, '').length < 8}
          onClick={async () => {
            setErro(null);
            setDiagnostico(null);
            setOcupado(true);
            try {
              setDiagnostico(JSON.stringify(await sondarBusca(ean), null, 2));
            } catch (e) {
              setErro((e as { message?: string }).message ?? 'A busca falhou.');
            } finally {
              setOcupado(false);
            }
          }}
          className="rounded border border-regua-2 px-3 py-1.5 text-xs hover:border-marca hover:text-marca disabled:opacity-50"
        >
          {ocupado ? 'Buscando…' : 'Buscar'}
        </button>
      </div>

      {/*
        O diagnóstico mostra o que a API respondeu, cru. É feio de propósito:
        ele existe para revelar contrato, não para ser bonito — e um resumo
        arrumado esconderia justamente o campo inesperado que interessa.
      */}
      {diagnostico && (
        <details open className="mt-1">
          <summary className="cursor-pointer text-xs font-medium text-marca">
            O que a API respondeu
          </summary>
          <pre className="mt-1 max-h-96 overflow-auto rounded bg-superficie-2 p-2 font-mono text-[0.65rem] leading-relaxed whitespace-pre-wrap">
            {diagnostico}
          </pre>
        </details>
      )}
    </li>
  );
}

function AbaIntegracoes() {
  const itens = useMemo(
    () => [
      {
        nome: 'E-mail (Resend)',
        estado: 'ligado' as const,
        detalhe:
          'Convite e recuperação de senha saem de nao-responda@profitlens.com.br. A chave vive no Secret Manager, nunca no pacote do navegador.',
      },
      {
        nome: 'Mercado Livre — catálogo e tarifa',
        estado: 'ligado' as const,
        detalhe:
          'Categorias e comissão saem da API com a chave da aplicação. A busca de anúncios continua bloqueada (pendência P1): toda a API passou a exigir token, e a coleta de preço de mercado ainda depende de entrada manual.',
      },
      {
        nome: 'OpenAI',
        estado: 'fase2' as const,
        detalhe: 'Leitura de resposta de fornecedor em texto livre. Entra com o Hermes, na Fase 2.',
      },
      {
        nome: 'WhatsApp Business',
        estado: 'fase3' as const,
        detalhe: 'Fase 3.',
      },
    ],
    [],
  );

  const CORES = {
    ligado: 'text-c-via bg-c-via/12',
    pendente: 'text-c-res bg-c-res/12',
    fase2: 'text-tinta-3 bg-tinta-3/12',
    fase3: 'text-tinta-3 bg-tinta-3/12',
  };
  const ROTULOS = {
    ligado: 'NO AR',
    pendente: 'PENDENTE',
    fase2: 'FASE 2',
    fase3: 'FASE 3',
  };

  return (
    <ul className="flex flex-col gap-2">
      <CartaoMercadoLivre />
      {itens.map((i) => (
        <li
          key={i.nome}
          className="flex flex-col gap-1 rounded-md border border-regua-2 bg-superficie px-4 py-3"
        >
          <span className="flex flex-wrap items-center gap-2">
            <Plug className="size-4 text-tinta-3" aria-hidden />
            <span className="font-medium">{i.nome}</span>
            <span
              className={`rounded px-1.5 py-0.5 font-mono text-[0.62rem] font-semibold ${CORES[i.estado]}`}
            >
              {ROTULOS[i.estado]}
            </span>
          </span>
          <p className="text-xs text-tinta-2">{i.detalhe}</p>
        </li>
      ))}
    </ul>
  );
}
